// src/main.tsx
import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {RouterProvider, createRouter} from '@tanstack/react-router';
import {QueryClient} from '@tanstack/react-query';

import {AppProviders} from '@/components/AppProviders';
import {ErrorBoundary} from '@/components/ErrorBoundary';
import {applyTheme} from '@/lib/apply-theme';
import {
  installChunkRecovery,
  installUpdateChecks,
  installUpdateOnNavigate,
  unregisterServiceWorkers,
  useSwUpdateStore,
} from '@/lib/sw-update';
import {useThemeStore} from '@/store/theme';
import {routeTree} from './routeTree.gen';
import '@fontsource/exo-2/latin-400.css';
import '@fontsource/exo-2/latin-500.css';
import '@fontsource/exo-2/latin-600.css';
import '@fontsource/exo-2/latin-700.css';
import './styles/globals.css';

// The inline script in index.html sets the theme class before first paint;
// re-apply from the (synchronously rehydrated) store to sync the theme-color
// meta, then keep <html> in sync on every toggle.
applyTheme(useThemeStore.getState().theme);
useThemeStore.subscribe((state) => applyTheme(state.theme));

// Armed before the app renders so a route chunk that 404s during the very
// first navigation after a deploy is recovered rather than shown as an error.
installChunkRecovery();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Treat server state as immediately stale so a view refetches whenever
      // it remounts (navigation), the tab regains focus, or the connection is
      // restored. With the old 5-minute staleTime, changes made elsewhere
      // (e.g. an approver acting on a request) didn't show until a manual
      // reload. Within-session mutations still update instantly via their
      // own invalidateQueries. Cached data is still served first while the
      // refetch runs in the background, so this stays snappy.
      //
      // Per-query `staleTime` overrides are legitimate ONLY on the whole-
      // collection "fetch-all" hooks that feed pickers/directories and prefill
      // (useProjectDirectory, useClientDirectory, useAllProjects,
      // useUserDirectory, useManagerDirectory, report-fill lookups): expensive
      // to re-page, rarely change mid-task, and invalidated explicitly by their
      // mutations. Single-record `useGet*` and on-page `useList*` queries must
      // inherit this global fresh-on-mount policy — DERP-151 was exactly the
      // stale-window bug those overrides reintroduced.
      //
      // **"Invalidated explicitly by their mutations" only covers ONE browser**
      // (DERP-258). `queryClient` is per-tab and there is no push channel, so an
      // admin's `invalidateQueries` never reaches the user whose form is reading
      // the same collection — that cache simply stays stale for the whole
      // override. Weigh an override against who ELSE edits the data: a directory
      // a user edits for themselves is fine; admin-maintained reference data
      // that prices a financial form is not.
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      retry: 1,
      gcTime: 30 * 60 * 1000,
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'offlineFirst',
    },
  },
});

const router = createRouter({
  routeTree,
  /**
   * Land at the top of a page you navigated TO, and back where you were when
   * you go back.
   *
   * Without this the window keeps whatever offset the previous page left: tap a
   * job near the bottom of a long list on a phone and the detail page opens
   * already scrolled — usually at its end, because the browser clamps an offset
   * the new, shorter page cannot honour. Nothing on screen explains it, and the
   * page looks like it opened backwards.
   *
   * A new location starts at the top, and a `popstate` — back or forward —
   * returns to the offset that location had. Doing it by hand would be a
   * `scrollTo(0, 0)` on every navigation, which gets the first half right and
   * throws the second half away.
   *
   * `scrollToTopSelectors` is not optional here, and this flag does nothing
   * without it: on a location with no stored offset the router scrolls
   * `window`, and the window is not what scrolls in this app. Every page lives
   * inside `AuthenticatedLayout`'s `<main class="overflow-y-auto">`, so
   * `window.scrollY` is always 0 and the reset lands on nothing. The symptom is
   * specifically a page you have opened BEFORE re-opening mid-way down: an
   * uncached one renders a short skeleton first, which clamps `<main>`'s own
   * scrollTop to 0 and hides the bug.
   *
   * Restoring back/forward needs no selector list — the router snapshots any
   * element that scrolls and keys it by `data-scroll-restoration-id`.
   */
  scrollRestoration: true,
  scrollToTopSelectors: ['[data-scroll-restoration-id="main"]'],
});

// Take a waiting build on the next route change rather than waiting for the
// user to notice the banner. Safe because a route change already discards the
// outgoing page's form state, and where a route guards unsaved work the
// blocker fires first so the navigation never resolves. See lib/sw-update.
installUpdateOnNavigate(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

async function unregisterStaleMockWorker() {
  // Cleans up any MSW worker left registered from a previous session where
  // VITE_USE_MOCKS was true. Without this, toggling mocks off still leaves the
  // browser intercepting requests until the user manually unregisters the
  // worker — confusing behaviour that looks like the env flag is broken.
  // Filtered by scriptURL so the PWA worker is untouched. Note: cleanup takes
  // effect on the next reload; the current page session may still see the
  // active worker.
  const dropped = await unregisterServiceWorkers((registration) =>
    (registration.active?.scriptURL ?? '').includes('mockServiceWorker.js'),
  );
  if (dropped > 0 && import.meta.env.DEV) {
    console.info('[mocks] Unregistered stale MSW worker — reload once more for a clean state');
  }
}

async function startMocks() {
  // Build-time gate: Vite replaces import.meta.env.PROD at build, so in
  // production this is `if (true) await unregisterStaleMockWorker(); return;`
  // and Rollup tree-shakes the dynamic imports below. The MSW worker module,
  // handlers, fixture users, and fixture passwords never enter the prod bundle.
  if (import.meta.env.PROD) {
    await unregisterStaleMockWorker();
    return;
  }
  if (import.meta.env.VITE_USE_MOCKS !== 'true') {
    await unregisterStaleMockWorker();
    return;
  }
  const {worker} = await import('./test/browser');
  await worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: {url: '/mockServiceWorker.js'},
  });
  // Hydrate mock state from localStorage and enable per-mutation persistence
  // so reset tokens, sessions, and updated passwords survive page reloads.
  // Has no effect in tests because this code path runs only in the browser.
  const {enableMockStatePersistence} = await import('./test/msw/state');
  enableMockStatePersistence();
  if (import.meta.env.DEV) {
    console.info('[mocks] MSW worker started — API calls intercepted by src/test/msw');
  }
}

async function bootstrap() {
  await startMocks();

  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element not found');

  createRoot(rootElement).render(
    <StrictMode>
      <ErrorBoundary>
        <AppProviders queryClient={queryClient}>
          <RouterProvider router={router} />
        </AppProviders>
      </ErrorBoundary>
    </StrictMode>,
  );
}

void bootstrap();

// Service worker registration
async function registerSW() {
  if ('serviceWorker' in navigator) {
    try {
      const {registerSW} = await import('virtual:pwa-register');
      // The returned `updateSW` goes unused: `takeNewBuild` drives the handover
      // itself, so it can cancel its own backstop when control changes.
      registerSW({
        onNeedRefresh() {
          // Raise the banner (see UpdatePrompt) so the user can take the new
          // build. This used to be a DEV-only console.log, which meant nothing
          // in production ever called updateSW: the new worker installed and
          // then waited forever, and because navigateFallback serves
          // index.html from the old precache, a plain refresh kept returning
          // the stale build. Only a hard refresh or closing every tab helped
          // — DERP-266.
          useSwUpdateStore.getState().markUpdateAvailable();
          if (import.meta.env.DEV) {
            console.log('[SW] New content available, refresh to update.');
          }
        },
        onOfflineReady() {
          if (import.meta.env.DEV) {
            console.log('[SW] App ready to work offline.');
          }
        },
        onRegisteredSW(swUrl, registration) {
          if (import.meta.env.DEV) {
            console.log('[SW] Registered:', swUrl);
          }
          if (registration) {
            const stopUpdateChecks = installUpdateChecks(registration);
            if (import.meta.hot) {
              import.meta.hot.dispose(stopUpdateChecks);
            }
          }
        },
        onRegisterError(error) {
          if (import.meta.env.DEV) {
            console.error('[SW] Registration error:', error);
          }
        },
      });
    } catch {
      // SW registration failed — app works fine without it
    }
  }
}

registerSW();
